import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useSubscription } from '../context/SubscriptionContext';
import { Button } from '../components/ui/button';
import MarketingHeader from '../components/marketing/MarketingHeader';
import { toast } from 'sonner';
import {
  Check, X, ChevronRight, LogIn, UserPlus,
  ArrowLeft, Star, Users, Shield, Brain, Mic, BarChart3,
  Target, MessageSquare, Phone, FileText, Mail, Clock,
  TrendingUp, Award, PieChart, Layers, Plus, ShoppingCart, Lock
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PLANS = [
  {
    id: 'free',
    name: 'Free',
    price: 0,
    period: '',
    tagline: 'Try it forever, no card required',
    highlight: false,
    features: [
      { text: '3 call analyses / month', included: true },
      { text: '30 transcript minutes', included: true },
      { text: 'Full AI scoring preview', included: true },
      { text: '10 tasks max', included: true },
      { text: 'Practice mode (1 session/mo)', included: true },
      { text: 'Pipeline', included: false },
      { text: 'Coaching insights', included: false },
      { text: 'Team features', included: false },
    ]
  },
  {
    id: 'solo',
    name: 'Solo',
    price: 24,
    period: '/mo',
    tagline: 'For individual reps who want to close more',
    highlight: false,
    features: [
      { text: '40 call analyses / month', included: true },
      { text: '600 transcript minutes', included: true },
      { text: '50 voice minutes', included: true },
      { text: 'Pipeline (up to 25 deals)', included: true },
      { text: 'Tasks + AI Task Recommendations', included: true },
      { text: 'Coaching insights', included: true },
      { text: 'Signal detection', included: true },
      { text: 'AI follow-up email', included: true },
      { text: 'Deal Advisor', included: false },
      { text: 'Team dashboard', included: false },
    ]
  },
  {
    id: 'pro',
    name: 'Pro',
    price: 59,
    period: '/mo',
    tagline: 'For high-performers and power users',
    highlight: true,
    badge: 'Most Popular',
    features: [
      { text: 'Everything in Solo', included: true, isGroup: true },
      { text: '150 call analyses / month', included: true },
      { text: '2,500 transcript minutes', included: true },
      { text: '250 standard + 30 realtime voice mins', included: true, category: 'voice' },
      { text: 'Unlimited Pipeline + forecasting', included: true },
      { text: 'Deal Advisor (AI)', included: true },
      { text: 'Risk Radar + Objection Intelligence', included: true },
      { text: 'AI Next Question Generator', included: true },
      { text: 'Custom scoring model', included: true },
      { text: 'Contact extraction', included: true },
      { text: 'Weekly AI reports', included: true },
    ]
  },
  {
    id: 'team',
    name: 'Team',
    price: 129,
    period: '/mo',
    perSeatPrice: 19,
    seatsIncluded: 5,
    tagline: 'For sales teams — scales with you',
    highlight: false,
    badge: 'Teams',
    features: [
      { text: 'Everything in Pro', included: true, isGroup: true },
      { text: '5 seats included + $19/seat extra', included: true },
      { text: 'Team dashboard & analytics', included: true },
      { text: 'Rep performance tracking', included: true },
      { text: 'Leaderboard', included: true },
      { text: 'Team Invitations (email)', included: true },
      { text: 'CRM export (HubSpot/Salesforce)', included: true },
      { text: 'Unlimited call analyses', included: true },
      { text: '5,000 transcript minutes', included: true },
      { text: '500 std + 100 realtime voice mins', included: true, category: 'voice' },
    ]
  },
  {
    id: 'scale',
    name: 'Scale',
    price: null,
    period: '',
    priceLabel: 'Custom',
    tagline: 'For sales orgs — dedicated support & SSO',
    highlight: false,
    badge: 'Enterprise',
    isContactSales: true,
    features: [
      { text: 'Everything in Team, unlimited', included: true, isGroup: true },
      { text: 'SSO (SAML)', included: true },
      { text: 'Custom AI training on your calls', included: true },
      { text: 'Dedicated success manager', included: true },
      { text: 'API access', included: true },
      { text: 'SLA 99.9% uptime', included: true },
      { text: 'Onboarding workshop', included: true },
      { text: 'Unlimited seats', included: true },
      { text: 'Unlimited everything', included: true },
    ]
  },
];

const ADD_ONS = [
  { id: 'voice_100', name: '+100 voice minutes', price: 15, priceLabel: '$15', type: 'voice_standard_minutes', amount: 100 },
  { id: 'voice_300', name: '+300 voice minutes', price: 39, priceLabel: '$39', type: 'voice_standard_minutes', amount: 300 },
  { id: 'transcript_1000', name: '+1,000 transcript minutes', price: 10, priceLabel: '$10', type: 'transcript_minutes', amount: 1000 },
  { id: 'transcript_3000', name: '+3,000 transcript minutes', price: 25, priceLabel: '$25', type: 'transcript_minutes', amount: 3000 },
];

const MODULES = [
  {
    icon: FileText,
    title: 'Transcription System',
    items: ['Record or upload sales calls', 'Whisper-powered transcription', 'Speaker separation', 'Structured storage'],
  },
  {
    icon: Brain,
    title: 'AI Analysis Engine',
    items: ['Deal score (0–100)', 'Intent & authority levels', 'Budget & timeline clarity', 'Objections & competitors', 'Structured JSON + readable report'],
  },
  {
    icon: Shield,
    title: 'Deal Risk Radar',
    items: ['Low buying intent detection', 'Missing decision maker alerts', 'Pricing resistance tracking', 'Risk score + mitigation strategy'],
  },
  {
    icon: Award,
    title: 'AI Coaching System',
    items: ['Talk-to-listen ratio', 'Question quality analysis', 'Objection handling eval', 'Strengths & improvement plan'],
  },
  {
    icon: MessageSquare,
    title: 'AI Next Question Generator',
    items: ['Context-aware questions', 'Discovery, objection & closing', '3–10 ranked next best questions', 'Based on deal stage + scoring'],
    badge: 'Pro+',
  },
  {
    icon: Phone,
    title: 'Voice Roleplay System',
    items: ['AI buyer personas (CEO, CFO, CTO)', 'Difficulty levels (easy → hard)', 'Real-time conversation mode', 'Post-session scoring & coaching'],
  },
];

export default function Pricing() {
  const { isAuthenticated, login } = useAuth();
  const { refreshSubscription } = useSubscription();
  const navigate = useNavigate();
  const [selecting, setSelecting] = useState(null);
  const [currentPlan, setCurrentPlan] = useState(null);
  const [billingCycle, setBillingCycle] = useState('monthly');
  const [currentCycle, setCurrentCycle] = useState('monthly');
  const [purchasingAddon, setPurchasingAddon] = useState(null);
  const [currentAddOns, setCurrentAddOns] = useState({});

  // Inject Ubuntu once — scoped to `.marketing-page` headings via CSS.
  useEffect(() => {
    const id = 'ludy-marketing-fonts';
    if (document.getElementById(id)) return;
    const link = document.createElement('link');
    link.id = id;
    link.rel = 'stylesheet';
    link.href = 'https://fonts.googleapis.com/css2?family=Ubuntu:ital,wght@0,400;0,500;0,700;1,400;1,700&display=swap';
    document.head.appendChild(link);
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      fetch(`${API_URL}/api/subscription/current`, { credentials: 'include' })
        .then(r => r.json())
        .then(data => {
          setCurrentPlan(data.plan);
          setCurrentCycle(data.billing_cycle || 'monthly');
          setBillingCycle(data.billing_cycle || 'monthly');
          setCurrentAddOns(data.add_ons || {});
        })
        .catch(() => {});
    }
  }, [isAuthenticated]);

  const handleSelectPlan = async (planId) => {
    if (!isAuthenticated) {
      navigate(`/auth?plan=${planId}&cycle=${billingCycle}`);
      return;
    }
    if (planId === currentPlan && billingCycle === currentCycle) return;

    setSelecting(planId);
    try {
      const res = await fetch(`${API_URL}/api/subscription/select`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ plan: planId, billing_cycle: billingCycle }),
      });
      if (!res.ok) throw new Error('Failed to update plan');
      const data = await res.json();
      setCurrentPlan(planId);
      setCurrentCycle(billingCycle);
      toast.success(data.message);
      refreshSubscription();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSelecting(null);
    }
  };

  const handlePurchaseAddon = async (addonId) => {
    if (!isAuthenticated) {
      navigate('/auth');
      return;
    }
    setPurchasingAddon(addonId);
    try {
      const res = await fetch(`${API_URL}/api/subscription/addon`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ addon_id: addonId }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Failed to purchase add-on');
      toast.success(data.message);
      // Refresh add-ons
      const subRes = await fetch(`${API_URL}/api/subscription/current`, { credentials: 'include' });
      const subData = await subRes.json();
      setCurrentAddOns(subData.add_ons || {});
      refreshSubscription();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setPurchasingAddon(null);
    }
  };

  const getPrice = (plan) => {
    if (plan.isContactSales) return { display: null, period: '', label: plan.priceLabel || 'Custom' };
    if (plan.price === 0) return { display: 0, period: '' };
    if (billingCycle === 'annual') {
      return {
        display: Math.round(plan.price * 0.8),
        original: plan.price,
        period: '/mo',
      };
    }
    return { display: plan.price, period: '/mo' };
  };

  return (
    <div className="marketing-page min-h-screen bg-background relative overflow-hidden">
      <div className="absolute inset-0 grid-overlay opacity-20 pointer-events-none" />
      <div
        className="absolute top-0 left-1/2 -translate-x-1/2 w-[1000px] h-[700px] pointer-events-none"
        style={{ background: 'radial-gradient(circle at center, rgba(14, 89, 68, 0.07) 0%, transparent 70%)' }}
      />

      {/* Shared marketing header — matches Landing */}
      <MarketingHeader solid />

      {/* Hero */}
      <section className="relative z-10 pt-16 pb-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 border border-primary/30 text-primary text-sm mb-6">
            <Layers className="w-4 h-4" />
            <span>Simple, transparent pricing</span>
          </div>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-foreground tracking-tight mb-4" data-testid="pricing-heading">
            Choose Your <span className="text-primary">Sales Edge</span>
          </h1>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            From solo reps to enterprise teams. Every plan comes with AI-powered call analysis.
            Scale as you grow.
          </p>

          {/* Billing Toggle */}
          <div className="mt-8 inline-flex items-center gap-3 p-1.5 rounded-full bg-secondary/50 border border-border/50" data-testid="billing-toggle">
            <button
              onClick={() => setBillingCycle('monthly')}
              data-testid="billing-monthly"
              className={`px-5 py-2 rounded-full text-sm font-medium transition-all duration-200 ${
                billingCycle === 'monthly'
                  ? 'bg-card text-foreground shadow-sm border border-border/50'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              Monthly
            </button>
            <button
              onClick={() => setBillingCycle('annual')}
              data-testid="billing-annual"
              className={`px-5 py-2 rounded-full text-sm font-medium transition-all duration-200 flex items-center gap-2 ${
                billingCycle === 'annual'
                  ? 'bg-card text-foreground shadow-sm border border-border/50'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              Annual
              <span className="px-2 py-0.5 rounded-full text-xs bg-emerald-500/15 text-emerald-600 border border-emerald-500/30 font-semibold">
                -20%
              </span>
            </button>
          </div>
        </div>
      </section>

      {/* Pricing Cards */}
      <section className="relative z-10 pb-20" data-testid="pricing-cards-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-5">
            {PLANS.map((plan, idx) => {
              const pricing = getPrice(plan);
              const isCurrent = currentPlan === plan.id && billingCycle === currentCycle;
              return (
              <div
                key={plan.id}
                data-testid={`plan-card-${plan.id}`}
                className={`
                  relative flex flex-col rounded-xl border p-6
                  transition-all duration-300 hover:-translate-y-1
                  ${plan.highlight
                    ? 'border-primary/70 bg-card shadow-[0_0_30px_rgba(56,189,248,0.12)]'
                    : 'border-border/50 bg-card hover:border-primary/30'
                  }
                `}
                style={{ animationDelay: `${idx * 0.08}s` }}
              >
                {plan.badge && (
                  <div className={`absolute -top-3 left-6 px-3 py-1 rounded-full text-xs font-semibold ${
                    plan.highlight
                      ? 'bg-primary text-primary-foreground'
                      : 'bg-secondary text-secondary-foreground border border-border'
                  }`}>
                    {plan.badge}
                  </div>
                )}

                <div className="mb-6">
                  <h3 className="font-heading text-xl font-bold text-foreground mb-1">{plan.name}</h3>
                  <p className="text-sm text-muted-foreground">{plan.tagline}</p>
                </div>

                <div className="mb-6">
                  {plan.isContactSales ? (
                    <div className="flex items-end gap-1">
                      <span className="font-heading text-4xl font-bold text-foreground" data-testid={`plan-price-${plan.id}`}>
                        Custom
                      </span>
                    </div>
                  ) : (
                    <div className="flex items-end gap-1">
                      <span className="font-heading text-4xl font-bold text-foreground" data-testid={`plan-price-${plan.id}`}>
                        ${pricing.display}
                      </span>
                      {pricing.period && <span className="text-muted-foreground text-sm mb-1">{pricing.period}</span>}
                    </div>
                  )}
                  {billingCycle === 'annual' && pricing.original && (
                    <div className="mt-1.5 flex items-center gap-2">
                      <span className="text-sm text-muted-foreground line-through">${pricing.original}/mo</span>
                      <span className="text-xs text-emerald-600 font-medium">Save ${(pricing.original - pricing.display) * 12}/yr</span>
                    </div>
                  )}
                  {plan.perSeatPrice && !plan.isContactSales && (
                    <p className="mt-2 text-xs text-muted-foreground">
                      {plan.seatsIncluded} seats included · +${billingCycle === 'annual' ? Math.round(plan.perSeatPrice * 0.8) : plan.perSeatPrice}/seat/mo extra
                    </p>
                  )}
                </div>

                <Button
                  data-testid={`plan-select-${plan.id}`}
                  onClick={() => plan.isContactSales ? window.location.href = 'mailto:sales@ludy.com?subject=Ludy%20Scale%20Plan%20Inquiry' : handleSelectPlan(plan.id)}
                  disabled={selecting === plan.id || isCurrent}
                  className={`w-full mb-6 ${
                    plan.highlight
                      ? 'bg-primary text-primary-foreground hover:bg-primary/90 shadow-glow'
                      : isCurrent
                        ? 'bg-secondary text-muted-foreground cursor-default'
                        : 'bg-secondary text-secondary-foreground hover:bg-secondary/80'
                  }`}
                >
                  {isCurrent
                    ? 'Current Plan'
                    : selecting === plan.id
                      ? 'Updating...'
                      : plan.isContactSales
                        ? 'Contact Sales'
                        : plan.price === 0
                          ? 'Get Started Free'
                          : 'Select Plan'}
                </Button>

                <div className="flex-1 space-y-3">
                  {plan.features.map((feature, fIdx) => (
                    <div
                      key={fIdx}
                      className={`flex items-start gap-2.5 text-sm ${
                        feature.category === 'voice' ? 'pt-1' : ''
                      } ${feature.isGroup ? 'font-medium text-primary' : ''}`}
                    >
                      {feature.included ? (
                        <Check className={`w-4 h-4 mt-0.5 flex-shrink-0 ${
                          feature.isGroup ? 'text-primary' : 'text-emerald-600'
                        }`} />
                      ) : (
                        <X className="w-4 h-4 mt-0.5 flex-shrink-0 text-muted-foreground/40" />
                      )}
                      <span className={feature.included ? 'text-foreground/90' : 'text-muted-foreground/50'}>
                        {feature.text}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Compare Plans Table */}
      <section className="relative z-10 pb-20" data-testid="compare-plans-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-8 text-center">
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-foreground mb-3">
              Compare All Features
            </h2>
            <p className="text-lg text-muted-foreground">See exactly what you get with each plan</p>
          </div>

          <div className="rounded-xl border border-border/50 bg-card overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full" data-testid="compare-table">
                <thead>
                  <tr className="border-b border-border/50">
                    <th className="text-left p-4 text-sm font-medium text-muted-foreground min-w-[200px]">Feature</th>
                    <th className="text-center p-4 text-sm font-medium text-muted-foreground">Free</th>
                    <th className="text-center p-4 text-sm font-medium text-muted-foreground">Starter</th>
                    <th className="text-center p-4 text-sm font-medium text-foreground bg-primary/5 border-x border-primary/20">Pro</th>
                    <th className="text-center p-4 text-sm font-medium text-muted-foreground">Team</th>
                  </tr>
                </thead>
                <tbody className="text-sm">
                  {[
                    { category: 'Limits', items: [
                      { name: 'Call Analyses', free: '5', starter: '50', pro: '150', team: 'Unlimited' },
                      { name: 'Transcript Minutes', free: '-', starter: '500', pro: '2,000', team: '5,000' },
                      { name: 'Standard Voice Minutes', free: '-', starter: '50', pro: '200', team: '500' },
                      { name: 'Realtime Voice Minutes', free: '-', starter: '-', pro: '30', team: '100' },
                      { name: 'Voice Training Sessions', free: '1 (10 min)', starter: 'Unlimited', pro: 'Unlimited', team: 'Unlimited' },
                    ]},
                    { category: 'Analysis', items: [
                      { name: 'Transcript & Summary', free: true, starter: true, pro: true, team: true },
                      { name: 'AI Scoring', free: false, starter: 'Basic', pro: 'Full V3', team: 'Full V3' },
                      { name: 'Signal Detection', free: false, starter: true, pro: true, team: true },
                      { name: 'Deal Risk Radar', free: false, starter: false, pro: true, team: true },
                      { name: 'Objection Intelligence', free: false, starter: false, pro: true, team: true },
                      { name: 'AI Next Question Generator', free: false, starter: false, pro: true, team: true },
                    ]},
                    { category: 'Sales Tools', items: [
                      { name: 'AI Coaching System', free: false, starter: false, pro: true, team: true },
                      { name: 'AI Follow-up Email', free: false, starter: true, pro: true, team: true },
                      { name: 'Contact Extraction (CRM)', free: false, starter: false, pro: true, team: true },
                      { name: 'Custom Scoring Model', free: false, starter: false, pro: true, team: true },
                      { name: 'Email Notifications', free: false, starter: false, pro: true, team: true },
                      { name: 'Weekly AI Reports', free: false, starter: false, pro: true, team: true },
                    ]},
                    { category: 'Pipeline', items: [
                      { name: 'Basic Deal Pipeline', free: false, starter: false, pro: true, team: true },
                      { name: 'Advanced Pipeline', free: false, starter: false, pro: false, team: true },
                      { name: 'Pipeline Forecasting', free: false, starter: false, pro: false, team: true },
                    ]},
                    { category: 'Team Features', items: [
                      { name: 'Team Dashboard', free: false, starter: false, pro: false, team: true },
                      { name: 'Rep Performance Tracking', free: false, starter: false, pro: false, team: true },
                      { name: 'Leaderboard', free: false, starter: false, pro: false, team: true },
                      { name: 'CRM Export (HubSpot/Salesforce)', free: false, starter: false, pro: false, team: true },
                    ]},
                  ].map((section, sIdx) => (
                    <React.Fragment key={`section-${sIdx}`}>
                      <tr className="border-t border-border/50 bg-secondary/30">
                        <td colSpan={5} className="px-4 py-2.5 text-xs font-semibold text-primary uppercase tracking-wider">{section.category}</td>
                      </tr>
                      {section.items.map((item, iIdx) => (
                        <tr key={`${sIdx}-${iIdx}`} className="border-t border-border/30 hover:bg-secondary/20 transition-colors">
                          <td className="px-4 py-3 text-foreground/90">{item.name}</td>
                          {['free', 'starter', 'pro', 'team'].map(planKey => {
                            const val = item[planKey];
                            return (
                              <td key={planKey} className={`text-center px-4 py-3 ${planKey === 'pro' ? 'bg-primary/5 border-x border-primary/10' : ''}`}>
                                {val === true ? (
                                  <Check className="w-4 h-4 text-emerald-600 mx-auto" />
                                ) : val === false ? (
                                  <X className="w-4 h-4 text-muted-foreground/30 mx-auto" />
                                ) : (
                                  <span className={`text-sm ${val === 'Unlimited' ? 'text-primary font-medium' : 'text-foreground/80'}`}>{val}</span>
                                )}
                              </td>
                            );
                          })}
                        </tr>
                      ))}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </section>

      {/* Add-ons */}
      <section className="relative z-10 pb-20" data-testid="addons-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="rounded-xl border border-border/50 bg-card p-8">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 rounded-lg bg-primary/10 border border-primary/30 flex items-center justify-center">
                <TrendingUp className="w-5 h-5 text-primary" />
              </div>
              <div>
                <h2 className="font-heading text-2xl font-bold text-foreground">Need More?</h2>
                <p className="text-sm text-muted-foreground">Add-on packages available for any paid plan</p>
              </div>
            </div>

            {/* Current add-ons summary */}
            {isAuthenticated && (currentAddOns.voice_standard_minutes > 0 || currentAddOns.transcript_minutes > 0) && (
              <div className="mb-4 mt-3 p-3 rounded-lg bg-emerald-500/10 border border-emerald-500/20" data-testid="current-addons-summary">
                <p className="text-xs font-medium text-emerald-600 mb-1">Active Add-ons</p>
                <div className="flex flex-wrap gap-3 text-sm text-foreground/80">
                  {currentAddOns.voice_standard_minutes > 0 && (
                    <span>+{currentAddOns.voice_standard_minutes} voice min</span>
                  )}
                  {currentAddOns.transcript_minutes > 0 && (
                    <span>+{currentAddOns.transcript_minutes} transcript min</span>
                  )}
                </div>
              </div>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-4">
              {ADD_ONS.map((addon) => (
                <div
                  key={addon.id}
                  data-testid={`addon-${addon.id}`}
                  className="flex flex-col p-4 rounded-lg bg-secondary/50 border border-border/30 hover:border-primary/30 transition-colors"
                >
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-sm text-foreground/90 font-medium">{addon.name}</span>
                    <span className="font-heading font-bold text-primary">{addon.priceLabel}</span>
                  </div>
                  <Button
                    size="sm"
                    data-testid={`addon-buy-${addon.id}`}
                    onClick={() => handlePurchaseAddon(addon.id)}
                    disabled={purchasingAddon === addon.id || (isAuthenticated && currentPlan === 'free')}
                    className="w-full bg-secondary/80 text-foreground/80 hover:bg-secondary border border-border/40 hover:border-primary/40 text-xs"
                  >
                    {purchasingAddon === addon.id ? (
                      'Adding...'
                    ) : isAuthenticated && currentPlan === 'free' ? (
                      <>
                        <Lock className="w-3 h-3 mr-1" />
                        Upgrade First
                      </>
                    ) : (
                      <>
                        <Plus className="w-3 h-3 mr-1" />
                        Add to Plan
                      </>
                    )}
                  </Button>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* System Modules */}
      <section className="relative z-10 pb-20 border-t border-border/50 pt-16" data-testid="modules-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-12">
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-foreground mb-3">
              Core System Modules
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl">
              Every module is designed to give you an unfair advantage in sales.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {MODULES.map((mod, idx) => (
              <div
                key={`k1-${idx}`}
                className="group p-6 rounded-xl bg-card border border-border/50 hover:border-primary/40 transition-all duration-300"
              >
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-lg bg-primary/10 border border-primary/30 flex items-center justify-center group-hover:shadow-glow transition-shadow duration-300">
                    <mod.icon className="w-5 h-5 text-primary" />
                  </div>
                  <h3 className="font-heading text-lg font-semibold text-foreground">{mod.title}</h3>
                  {mod.badge && (
                    <span className="ml-auto px-2 py-0.5 rounded text-xs bg-primary/10 text-primary border border-primary/30">
                      {mod.badge}
                    </span>
                  )}
                </div>
                <ul className="space-y-2">
                  {mod.items.map((item, iIdx) => (
                    <li key={iIdx} className="flex items-center gap-2 text-sm text-muted-foreground">
                      <div className="w-1 h-1 rounded-full bg-primary flex-shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pipeline System */}
      <section className="relative z-10 pb-20" data-testid="pipeline-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Pro Pipeline */}
            <div className="rounded-xl border border-border/50 bg-card p-8">
              <div className="flex items-center gap-2 mb-4">
                <PieChart className="w-5 h-5 text-primary" />
                <h3 className="font-heading text-xl font-bold text-foreground">Pro Pipeline</h3>
                <span className="px-2 py-0.5 rounded text-xs bg-primary/10 text-primary border border-primary/30">Pro</span>
              </div>
              <ul className="space-y-2.5">
                {['Auto-create deal from call', 'Stages: Lead → Qualified → Demo → Proposal → Closed', 'Auto-fill: company, contact, pain points', 'Basic deal scoring', 'Calls update deals automatically'].map((item, i) => (
                  <li key={`k1-${i}`} className="flex items-start gap-2.5 text-sm text-muted-foreground">
                    <Check className="w-4 h-4 mt-0.5 text-emerald-600 flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>

            {/* Team Pipeline */}
            <div className="rounded-xl border border-primary/30 bg-card p-8 shadow-[0_0_20px_rgba(56,189,248,0.06)]">
              <div className="flex items-center gap-2 mb-4">
                <PieChart className="w-5 h-5 text-primary" />
                <h3 className="font-heading text-xl font-bold text-foreground">Team Pipeline</h3>
                <span className="px-2 py-0.5 rounded text-xs bg-primary text-primary-foreground">Team</span>
              </div>
              <ul className="space-y-2.5">
                {['Everything in Pro Pipeline', 'Deal ownership & team visibility', 'Pipeline analytics & health', 'Win probability forecasting', 'Risk scoring per deal', 'Stage suggestions based on AI', 'Next step recommendations'].map((item, i) => (
                  <li key={`k2-${i}`} className="flex items-start gap-2.5 text-sm text-muted-foreground">
                    <Check className="w-4 h-4 mt-0.5 text-primary flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Team Intelligence */}
      <section className="relative z-10 pb-20 border-t border-border/50 pt-16" data-testid="team-intel-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-8 flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-primary/10 border border-primary/30 flex items-center justify-center">
              <Users className="w-5 h-5 text-primary" />
            </div>
            <div>
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground">Team Intelligence Layer</h2>
              <p className="text-sm text-muted-foreground">The competitive moat. Team plan only.</p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {[
              {
                title: 'Team Benchmarking',
                items: ['Compare rep performance', 'Identify top performers', 'Analyze what top reps do differently'],
              },
              {
                title: 'Aggregate Intelligence',
                items: ['Team-wide strengths & weaknesses', 'Common mistakes detection', 'Missed opportunity patterns'],
              },
              {
                title: 'Question Intelligence',
                items: ['Top 10 performing questions/month', 'Questions that lead to closed deals', 'Categorized: discovery, objection, closing', 'Context & variation suggestions'],
              },
              {
                title: 'Pipeline Forecasting',
                items: ['Predict deal win probability', 'Identify at-risk deals', 'Revenue forecast from pipeline data'],
              },
            ].map((block, idx) => (
              <div key={`k2-${idx}`} className="p-6 rounded-xl bg-card border border-border/50">
                <h3 className="font-heading text-lg font-semibold text-foreground mb-3">{block.title}</h3>
                <ul className="space-y-2">
                  {block.items.map((item, i) => (
                    <li key={`k3-${i}`} className="flex items-center gap-2 text-sm text-muted-foreground">
                      <Star className="w-3.5 h-3.5 text-primary flex-shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative z-10 py-16 border-t border-border/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="relative rounded-2xl bg-card border border-border/50 p-12 overflow-hidden">
            <div
              className="absolute inset-0 pointer-events-none"
              style={{ background: 'radial-gradient(circle at 30% 50%, rgba(14, 89, 68, 0.06) 0%, transparent 50%)' }}
            />
            <div className="relative z-10 max-w-2xl">
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-foreground mb-4">
                Ready to Close More Deals?
              </h2>
              <p className="text-lg text-muted-foreground mb-8">
                Start with Free. Upgrade when your team is ready for the full AI sales operating system.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Button
                  size="lg"
                  onClick={() => navigate('/auth')}
                  className="bg-primary text-primary-foreground hover:bg-primary/90 shadow-glow"
                  data-testid="pricing-cta-btn"
                >
                  Get Started Free
                  <ChevronRight className="w-5 h-5 ml-2" />
                </Button>
                <Button
                  size="lg"
                  variant="outline"
                  onClick={login}
                  className="border-border hover:bg-secondary"
                >
                  Sign In with Google
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="relative z-10 border-t border-border/50 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <img src="/ludy-wordmark.png" alt="Ludy" className="h-5" />
            </div>
            <p className="text-sm text-muted-foreground">Built with AI. Designed for sales excellence.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
